import { getOrganizationCosts } from './usage-endpoints';
import { getCache } from '../indexeddb-cache';
import { withProviderResourceLock, LIVE_RESOURCE_TTL_MS, type ResourceSnapshot } from '../provider-resource-cache';
import { subtractTimeRanges, splitTimeRangesByMaxSpan } from '../range-coverage';
import { DAY_SECONDS, finalizationBoundary, RETAINED_FRESH_UNTIL } from './history-policy';
import type { ApiResponse, OpenAiUsageBucket, OpenAiUsagePage } from './types';

const ENDPOINT = 'cost_day_v2'; // daily, grouped by project_id + line_item
interface CostDay {
  startTime: number;
  endTime: number;
  fetchedAt: number;
  freshUntil: number;
  finalized: boolean;
  buckets: OpenAiUsageBucket[];
}

/** Reuse overlapping days, including empty days; batch adjacent gaps into at
 * most 180 days per request. No full-history download on a range change. */
export const getCachedOpenAiCosts = (
  apiKey: string, startTime: number, endTime: number, credentialRevision: string, forceRefresh = false
): Promise<ResourceSnapshot<ApiResponse<OpenAiUsagePage>>> => withProviderResourceLock(
  `openai:costs:${credentialRevision}`,
  async () => {
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime
      || startTime % DAY_SECONDS !== 0 || endTime % DAY_SECONDS !== 0) {
      throw new Error('Cost cache requires a non-empty UTC-day-aligned range');
    }
    const cache = getCache();
    const paramsFor = (day: CostDay) => ({ credentialRevision, bucketWidth: '1d',
      startTime: day.startTime, endTime: day.endTime });
    const cached = await cache.listRange<CostDay>('openai', ENDPOINT, credentialRevision,
      '1d', startTime, endTime, DAY_SECONDS);
    const days: CostDay[] = [];
    for (const entry of cached) {
      const day = entry.data;
      if (!day || day.startTime !== entry.params?.startTime || day.endTime !== day.startTime + DAY_SECONDS
        || !Array.isArray(day.buckets) || !Number.isFinite(day.fetchedAt)) continue;
      const finalized = day.endTime <= finalizationBoundary('1d', day.fetchedAt);
      if (forceRefresh && !finalized) {
        await cache.delete('openai', ENDPOINT, paramsFor(day));
      } else if (finalized || day.freshUntil > Date.now()) {
        days.push(day);
      }
    }
    const gaps = subtractTimeRanges({ start: startTime, end: endTime },
      days.map(day => ({ start: day.startTime, end: day.endTime })));
    for (const range of splitTimeRangesByMaxSpan(gaps, 180 * DAY_SECONDS)) {
      const fetchedAt = Date.now();
      const response = await getOrganizationCosts(apiKey, range.start, range.end,
        { groupBy: ['project_id', 'line_item'] });
      if (!response.success || !Array.isArray(response.data?.data)) {
        // Successful earlier batches stay reusable; the failed gap stays missing.
        return { value: { success: false, error: response.error ?? 'Invalid cost response' },
          fetchedAt, freshUntil: fetchedAt };
      }
      const byDay = new Map<number, OpenAiUsageBucket[]>();
      for (const bucket of response.data.data) {
        const day = Math.floor(bucket.start_time / DAY_SECONDS) * DAY_SECONDS;
        const existing = byDay.get(day) ?? [];
        existing.push(bucket);
        byDay.set(day, existing);
      }
      const batch: CostDay[] = [];
      for (let start = range.start; start < range.end; start += DAY_SECONDS) {
        const end = start + DAY_SECONDS;
        const finalized = end <= finalizationBoundary('1d', fetchedAt);
        batch.push({ startTime: start, endTime: end, fetchedAt, finalized,
          freshUntil: finalized ? RETAINED_FRESH_UNTIL : fetchedAt + LIVE_RESOURCE_TTL_MS,
          buckets: byDay.get(start) ?? [] });
      }
      await cache.setMany('openai', ENDPOINT, batch.map(day => ({ data: day, params: paramsFor(day),
        ttl: day.finalized ? null : Math.max(1, day.freshUntil - Date.now()) })));
      days.push(...batch);
    }
    days.sort((a, b) => a.startTime - b.startTime);
    return { value: { success: true, data: { data: days.flatMap(day => day.buckets), has_more: false } },
      fetchedAt: Math.max(...days.map(day => day.fetchedAt)),
      freshUntil: Math.min(RETAINED_FRESH_UNTIL, ...days.map(day => day.freshUntil)) };
  }, forceRefresh, `${startTime}:${endTime}`
);
